import { apiService } from './api';

export interface SecurityScan {
  id: string;
  targetUrl: string;
  type: 'vulnerability' | 'penetration' | 'compliance' | 'audit';
  status: 'pending' | 'in-progress' | 'completed' | 'failed';
  startTime: Date;
  endTime?: Date;
  results?: ScanResults;
  createdBy: string;
}

export interface ScanResults {
  totalVulnerabilities: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  vulnerabilities: Vulnerability[];
  complianceScore?: number;
  recommendations: string[];
}

export interface Vulnerability {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  cveId?: string;
  affectedComponent: string;
  remediation: string;
  references: string[];
  discovered: Date;
  status: 'open' | 'in-progress' | 'resolved' | 'false-positive';
}

export interface ComplianceReport {
  id: string;
  framework: 'PCI-DSS' | 'HIPAA' | 'GDPR' | 'SOC2' | 'ISO27001';
  score: number;
  status: 'compliant' | 'non-compliant' | 'partial';
  findings: ComplianceFinding[];
  generatedAt: Date;
}

export interface ComplianceFinding {
  controlId: string;
  controlName: string;
  status: 'pass' | 'fail' | 'partial';
  evidence: string;
  recommendation?: string;
}

export interface ThreatIntelligence {
  id: string;
  type: 'malware' | 'phishing' | 'exploit' | 'botnet' | 'other';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  indicators: string[];
  mitigation: string[];
  source: string;
  publishedAt: Date;
}

class SecurityService {
  // Vulnerability Scanning
  async startVulnerabilityScan(targetUrl: string): Promise<SecurityScan> {
    return await apiService.post<SecurityScan>('/security/scans', {
      targetUrl,
      type: 'vulnerability',
    });
  }

  async getScanStatus(scanId: string): Promise<SecurityScan> {
    return await apiService.get<SecurityScan>(`/security/scans/${scanId}`);
  }

  async getScans(filters?: {
    status?: string;
    type?: string;
    startDate?: Date;
    endDate?: Date;
  }): Promise<SecurityScan[]> {
    return await apiService.get<SecurityScan[]>('/security/scans', { params: filters });
  }

  async getScanResults(scanId: string): Promise<ScanResults> {
    return await apiService.get<ScanResults>(`/security/scans/${scanId}/results`);
  }

  // Vulnerability Management
  async getVulnerabilities(filters?: {
    severity?: string;
    status?: string;
    component?: string;
  }): Promise<Vulnerability[]> {
    return await apiService.get<Vulnerability[]>('/security/vulnerabilities', {
      params: filters,
    });
  }

  async getVulnerability(id: string): Promise<Vulnerability> {
    return await apiService.get<Vulnerability>(`/security/vulnerabilities/${id}`);
  }

  async updateVulnerabilityStatus(
    id: string,
    status: Vulnerability['status']
  ): Promise<Vulnerability> {
    return await apiService.patch<Vulnerability>(`/security/vulnerabilities/${id}`, {
      status,
    });
  }

  // Compliance
  async generateComplianceReport(framework: ComplianceReport['framework']): Promise<ComplianceReport> {
    return await apiService.post<ComplianceReport>('/security/compliance/reports', {
      framework,
    });
  }

  async getComplianceReports(): Promise<ComplianceReport[]> {
    return await apiService.get<ComplianceReport[]>('/security/compliance/reports');
  }

  async getComplianceReport(id: string): Promise<ComplianceReport> {
    return await apiService.get<ComplianceReport>(`/security/compliance/reports/${id}`);
  }

  // Threat Intelligence
  async getThreatIntelligence(filters?: {
    type?: string;
    severity?: string;
    dateFrom?: Date;
    dateTo?: Date;
  }): Promise<ThreatIntelligence[]> {
    return await apiService.get<ThreatIntelligence[]>('/security/threats', {
      params: filters,
    });
  }

  async getThreatDetails(id: string): Promise<ThreatIntelligence> {
    return await apiService.get<ThreatIntelligence>(`/security/threats/${id}`);
  }

  // Security Audit
  async startSecurityAudit(scope: {
    includeNetwork?: boolean;
    includeApplications?: boolean;
    includeInfrastructure?: boolean;
    includeCompliance?: boolean;
  }): Promise<SecurityScan> {
    return await apiService.post<SecurityScan>('/security/audit', scope);
  }

  // Penetration Testing
  async startPenetrationTest(config: {
    targetUrl: string;
    testType: 'black-box' | 'white-box' | 'gray-box';
    scope: string[];
  }): Promise<SecurityScan> {
    return await apiService.post<SecurityScan>('/security/pentest', config);
  }

  // Security Metrics
  async getSecurityMetrics(): Promise<{
    securityScore: number;
    vulnerabilityTrend: Array<{ date: string; count: number }>;
    complianceStatus: Record<string, number>;
    threatLevel: 'low' | 'medium' | 'high' | 'critical';
    lastScanDate: Date;
    openVulnerabilities: number;
    resolvedThisMonth: number;
  }> {
    return await apiService.get('/security/metrics');
  }

  // Export Reports
  async exportReport(type: 'pdf' | 'csv' | 'json', reportId: string): Promise<Blob> {
    const response = await apiService.get(`/security/reports/${reportId}/export`, {
      params: { format: type },
      responseType: 'blob',
    });
    return response as Blob;
  }
}

export const securityService = new SecurityService();